import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterGoodwood() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-goodwood",
    name: "Painters Goodwood",
    location: "Goodwood, Cape Town",
    rating: 4.6,
    reviews: 134,
    specialties: ["Residential Painting", "Commercial Projects", "Interior Design", "Exterior Restoration"],
    image: "https://public.readdy.ai/ai/img_res/1525e2a8ae52172751ef1fd8bc40a831.jpg",
    verified: true,
    yearsExperience: 12,
    website: "https://paintersgoodwood.co.za",
    email: "info@paintersgoodwood.co.za",
    description: "Trusted painting services in Goodwood and the northern suburbs of Cape Town. We specialize in residential and commercial painting projects, delivering quality workmanship and exceptional customer service.",
    services: [
      "Interior House Painting",
      "Exterior House Painting", 
      "Commercial Painting",
      "Office Painting",
      "Roof Painting",
      "Waterproofing",
      "Damp Proofing",
      "Color Consultation"
    ],
    serviceAreas: ["Goodwood", "Parow", "Bellville", "Elsies River", "Ravensmead", "Thornton", "Northern Suburbs", "Cape Town"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20interior%20living%20room%20painted%20by%20professional%20painters%20in%20Goodwood%20Cape%20Town%2C%20modern%20suburban%20home%20with%20fresh%20walls%2C%20quality%20paint%20finish%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=goodwood-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20house%20painting%20project%20in%20Goodwood%20Cape%20Town%2C%20suburban%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20northern%20suburbs%20setting%2C%20well-maintained%20property&width=400&height=300&seq=goodwood-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Commercial%20painting%20project%20in%20Goodwood%2C%20professional%20painters%20working%20on%20office%20building%2C%20quality%20commercial%20paint%20finish%2C%20modern%20business%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=goodwood-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Kitchen%20interior%20painting%20in%20Goodwood%20suburban%20home%2C%20fresh%20painted%20walls%2C%20modern%20kitchen%20design%2C%20quality%20paint%20finish%2C%20bright%20clean%20space%2C%20professional%20painting%20work&width=400&height=300&seq=goodwood-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Mark Johnson",
        rating: 5,
        comment: "Excellent work on our Goodwood home. The team was professional, punctual, and delivered outstanding results. Highly recommend their services."
      },
      {
        name: "Linda Petersen", 
        rating: 4,
        comment: "Great attention to detail and very clean workers. Our office building looks fantastic and the project was completed on time."
      },
      {
        name: "Ahmed Hassan",
        rating: 5,
        comment: "Professional service from start to finish. They transformed our home and the quality of work exceeded our expectations."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Goodwood | Professional House Painting Services Cape Town | Residential & Commercial";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Goodwood offers professional painting services in Goodwood, Cape Town. Specializing in residential and commercial painting, interior and exterior work. Trusted local painting contractors with quality results.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Goodwood, house painters Goodwood, painting contractors Cape Town, residential painting, commercial painting services');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20residential%20home%20in%20Goodwood%20Cape%20Town%20northern%20suburbs%2C%20suburban%20neighborhood%20setting%20with%20tree-lined%20streets%2C%20painters%20in%20clean%20uniforms%20with%20quality%20equipment%2C%20well-maintained%20property%20with%20modern%20architecture%2C%20bright%20natural%20lighting%2C%20high%20quality%20residential%20painting%20project&width=1200&height=600&seq=goodwood-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Goodwood - Your Trusted Northern Suburbs Painting Specialists</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description} With over 12 years of experience serving the northern suburbs community, we have built a reputation for excellence, reliability, and outstanding customer service that has made us the preferred choice for homeowners and businesses throughout the area.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Professional Painting Services in Goodwood</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Located in the heart of Cape Town's northern suburbs, Goodwood represents a diverse community with a rich mix of residential and commercial properties. Our team understands the unique characteristics of this area, from older established homes that require careful restoration work to modern developments that benefit from contemporary painting techniques. We have developed specialized approaches for each type of property, ensuring that every project receives the attention and expertise it deserves.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Why Choose Our Goodwood Painting Services</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our commitment to excellence extends beyond just applying paint to walls. We believe in building lasting relationships with our clients through transparent communication, reliable service delivery, and exceptional workmanship. Every member of our team is trained in the latest painting techniques and safety protocols, ensuring that your project is completed to the highest professional standards while maintaining a safe and clean work environment.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Northern Suburbs Expertise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The northern suburbs of Cape Town present unique opportunities and challenges for painting contractors. The area's diverse architecture, ranging from Cape Dutch heritage homes to modern suburban developments, requires a deep understanding of different building materials, architectural styles, and appropriate painting techniques. Our extensive experience in Goodwood and surrounding areas has given us invaluable insights into the specific requirements of northern suburbs properties.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Quality Assurance and Customer Satisfaction</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We maintain rigorous quality control standards throughout every project, from initial surface preparation through final inspection. Our quality assurance process includes regular progress reviews, material quality checks, and comprehensive final inspections to ensure that every aspect of the work meets our exacting standards. We stand behind our work with comprehensive warranties and are committed to addressing any concerns promptly and professionally.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Environmental Responsibility and Safety</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  As responsible contractors serving the Goodwood community, we prioritize environmental sustainability and workplace safety in all our operations. We use low-VOC and eco-friendly paint products whenever possible, implement proper waste disposal procedures, and maintain comprehensive safety protocols to protect both our workers and your property. Our commitment to environmental responsibility extends to our material selection, application techniques, and cleanup procedures.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Comprehensive Project Planning and Execution</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Every painting project in Goodwood begins with a thorough consultation and assessment phase where we evaluate your specific needs, preferences, and budget considerations. We provide detailed project proposals that outline all aspects of the work, including material specifications, timeline estimates, and cost breakdowns. Our project management approach ensures clear communication throughout the process, keeping you informed of progress and addressing any questions or concerns promptly.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Local Community Commitment</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  As a locally-based painting contractor, we are deeply committed to contributing positively to the Goodwood and northern suburbs community. We source materials from local suppliers whenever possible, employ local workers, and actively participate in community initiatives. Our success is directly tied to the satisfaction of our neighbors and the continued growth and prosperity of the areas we serve.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20residential%20home%20in%20Goodwood%20Cape%20Town%2C%20northern%20suburbs%20property%20with%20quality%20paint%20finish%2C%20painters%20in%20uniform%20with%20professional%20equipment%2C%20traditional%20suburban%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=goodwood-sidebar-image&orientation=landscape"
                      alt="Professional painters Goodwood residential house painting services Cape Town northern suburbs"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Painting Services in Goodwood</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Residential Painting Excellence</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our residential painting services in Goodwood are designed to transform your home into a beautiful, comfortable living space that reflects your personal style and enhances your property value. We work with homeowners throughout the northern suburbs to deliver exceptional results that exceed expectations while respecting budgets and timelines.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with premium materials and expert northern suburbs application techniques.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Interior Painting Transformation</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our interior painting services in Goodwood focus on creating beautiful, healthy living environments that enhance your daily life. We use premium, low-VOC paints that provide excellent coverage and durability while maintaining superior indoor air quality. Our color consultation services help you select the perfect palette that complements your furniture, lighting, and personal style preferences.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We understand that interior painting projects can be disruptive to daily routines, so we work efficiently and maintain clean, organized work areas to minimize inconvenience. Our team is experienced in working around furniture and can complete projects in phases if needed to accommodate your family's schedule and lifestyle requirements.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Commercial Painting Solutions</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our commercial painting services in Goodwood cater to businesses, retail establishments, office buildings, and industrial facilities throughout the northern suburbs. We understand the importance of maintaining professional appearances while minimizing business disruption. Our team works flexible schedules, including evenings and weekends, to accommodate business operations and ensure projects are completed on time and within budget.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    For commercial clients, we offer comprehensive maintenance painting programs that help preserve property values and maintain professional appearances year-round. These programs include regular inspections, touch-up services, and scheduled repainting cycles that prevent minor issues from becoming major problems.
                  </p>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Specialized Coating Services</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Beyond traditional painting, we offer specialized coating services including waterproofing, damp proofing, and protective finishes. Our waterproofing services are particularly important in the Cape Town climate, where moisture management is crucial for building longevity. We use advanced waterproofing systems that provide comprehensive protection while maintaining structural integrity and aesthetic appeal.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Painting Projects in Goodwood</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent painting projects throughout Goodwood and the northern suburbs. Each project demonstrates our commitment to quality workmanship and our expertise in handling diverse painting challenges across different property types and architectural styles.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Goodwood Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional northern suburbs painting with premium materials and expert application techniques.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout Goodwood and the northern suburbs have to say about our painting services. Our commitment to excellence and customer satisfaction is reflected in these genuine reviews from homeowners and business owners in the area.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Goodwood</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to transform your Goodwood property with professional painting services? Contact us today for a free consultation and quote. We're here to help you achieve beautiful, long-lasting results that enhance your northern suburbs lifestyle.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding northern suburbs areas</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Quote Today</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation quote for your Goodwood painting project. Our experienced team will assess your property and provide detailed recommendations tailored to northern suburbs conditions.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
